import React, { useState } from 'react';
import { Check, Award, Users, Heart, Shield, Banknote } from 'lucide-react';
import { Button } from './ui/button';
import BookingCalendar from './BookingCalendar';

const WhyBookSection = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  
  const reasons = [
    {
      icon: Banknote,
      title: "Claim Money Back",
      description: "As our client, you CAN CLAIM MONEY BACK via your personal insurance plan. Get top-tier therapeutic treatment and benefit financially from your investment in your health.",
      highlight: true
    },
    {
      icon: Award,
      title: "Established Since 2009",
      description: "Dublin Holistic Massage has built a reputation for providing exceptional massage experiences for over 14 years."
    },
    {
      icon: Users,
      title: "Male & Female Therapists",
      description: "Choose from our team of experienced male and female massage therapists to suit your comfort and preference."
    },
    {
      icon: Heart,
      title: "Tailored to Your Needs",
      description: "Whether you're seeking relaxation, relief from muscular tension, or a holistic approach to wellness - we're here to help."
    },
    {
      icon: Shield,
      title: "Professionally Registered",
      description: "Our therapists are registered with IMTA (Irish Massage Therapists Association) and members of NRRI (National Register of Reflexologists Ireland)."
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-sage-50 to-white relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-sage-100/50 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-warm-100/50 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-sage-600 font-medium tracking-wider uppercase text-sm mb-4">
            Why Choose Us
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-800 mb-6">
            Why Book a Massage with
            <span className="block text-sage-600">Dublin Holistic Massage?</span>
          </h2>
          <p className="text-stone-600 text-lg">
            Your ultimate destination for an exceptional massage experience. 
            Here's why we stand out as absolutely amazing.
          </p>
        </div>

        {/* USP Highlight - Insurance Claim */}
        <div className="bg-gradient-to-r from-sage-600 to-sage-700 rounded-3xl p-8 md:p-12 mb-12 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Banknote size={18} />
                <span>Our Unique Advantage</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold mb-4">
                Claim Money Back on Your Massage
              </h3>
              <p className="text-sage-100 text-lg leading-relaxed mb-6">
                What truly makes DHM special is that you as our client <strong className="text-white">CAN CLAIM MONEY BACK</strong> via your personal insurance plan. This means you not only get top-tier therapeutic treatment but can also <strong className="text-white">benefit financially</strong> from your investment in your health.
              </p>
              <Button
                onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })}
                className="bg-white text-sage-700 hover:bg-sage-50 px-6 py-3 rounded-full font-medium transition-all duration-300"
              >
                Ask About Insurance Claims
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {['Irish Life Health', 'Laya Healthcare', 'VHI Healthcare', 'Westfield Health', 'Level'].map((provider) => (
                <div key={provider} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                  <Check size={20} className="mx-auto mb-2 text-sage-200" />
                  <span className="text-sm font-medium">{provider}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Other Reasons Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.slice(1).map((reason) => (
            <div
              key={reason.title}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
            >
              <div className="w-14 h-14 bg-sage-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-sage-600 transition-colors">
                <reason.icon size={28} className="text-sage-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-semibold text-stone-800 mb-2">
                {reason.title}
              </h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                {reason.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center gap-4 bg-white rounded-2xl px-6 py-4 shadow-lg mb-8" data-testid="tripadvisor-award-block">
            <img
              src="/tripadvisor-2026.png"
              alt="Tripadvisor Travellers' Choice 2026 award"
              className="w-14 h-[4.4rem] object-contain"
            />
            <div className="text-left">
              <p className="text-stone-800 font-semibold leading-tight">Tripadvisor Travellers&rsquo; Choice 2026</p>
              <p className="text-stone-500 text-sm">Awarded for consistently earning outstanding reviews</p>
            </div>
          </div>
          <p className="text-stone-600 mb-6">
            Experience the difference that professional, registered therapists make.
          </p>
          <Button
            onClick={() => setIsBookingOpen(true)}
            className="bg-sage-600 hover:bg-sage-700 text-white px-8 py-4 rounded-full text-lg transition-all duration-300 hover:shadow-lg"
          >
            Book Your Massage Today
          </Button>
        </div>
      </div>
      
      {/* Booking Modal */}
      <BookingCalendar isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </section>
  );
};

export default WhyBookSection;
